import React, { useCallback, useEffect, useState } from 'react';

import { useAuth } from '~/auth/hooks/useAuth';

import { Alert, Box, Button, Container, Tab, Tabs } from '@mui/material';

import ErrorDisplay from '~/components/Common/ErrorDisplay';
import Spinner from '~/components/Common/Spinner';

import type { TagType } from '~/consts/tags';
import { TAG_TYPE_CAMPAIGN, TAG_TYPE_HOLIDAY } from '~/consts/tags';

import { useMarketingTagsCatalog } from '~/hooks/apis/marketing/useMarketingTagsCatalog';

import OffersService from '~/services/OffersService';

import TagsComponent from './TagsComponent';
import { MARKETING_VERTICALS, type MarketingVerticalConfig, type MarketingVerticalId } from './verticalConfig';

function sortTags(tags: Array<App.MarketingTag>) {
  return [...tags].sort((tagA, tagB) => tagA.name.localeCompare(tagB.name));
}

export default function TagsContainer() {
  const { authenticated, loading: authLoading } = useAuth();
  const [activeVerticalId, setActiveVerticalId] = useState<MarketingVerticalId>(MARKETING_VERTICALS[0].id);
  const [hotelsTagType, setHotelsTagType] = useState<TagType>(TAG_TYPE_CAMPAIGN);
  const [campaigns, setCampaigns] = useState<Array<App.MarketingTag>>([]);
  const [holidayTypes, setHolidayTypes] = useState<Array<App.MarketingTag>>([]);

  const {
    data: tagsCatalog,
    isLoading: isTagsLoading,
    isError: isTagsError,
    error: tagsError,
    refetch: refetchTags,
    isFetching: isTagsFetching,
  } = useMarketingTagsCatalog({
    enabled: authenticated && !authLoading,
  });

  useEffect(() => {
    if (tagsCatalog) {
      setCampaigns(tagsCatalog.campaigns);
      setHolidayTypes(tagsCatalog.holidayTypes);
    }
  }, [tagsCatalog]);

  const handleDeleteTag = async (type: TagType, id: number): Promise<void> => {
    try {
      await OffersService.deleteTag(type, id);

      switch (type) {
        case TAG_TYPE_CAMPAIGN:
          setCampaigns((current) => sortTags(current.filter((tag) => tag.id !== id)));
          break;
        case TAG_TYPE_HOLIDAY:
          setHolidayTypes((current) => sortTags(current.filter((tag) => tag.id !== id)));
          break;
      }
    } catch {
      alert("Something went wrong. Can't delete tag");
    }
  };

  const handleUpdateTag = async (type: TagType, id: number, updatedName: string) => {
    const { result: updatedTag } = await OffersService.updateTag(type, id, updatedName);

    switch (type) {
      case TAG_TYPE_CAMPAIGN:
        setCampaigns((current) => sortTags(current.map((tag) => (tag.id === id ? updatedTag : tag))));
        break;
      case TAG_TYPE_HOLIDAY:
        setHolidayTypes((current) => sortTags(current.map((tag) => (tag.id === id ? updatedTag : tag))));
        break;
    }
  };

  const handleCreateTag = async (type: TagType, name: string): Promise<App.MarketingTag> => {
    const response = await OffersService.createTag(type, name);

    switch (type) {
      case TAG_TYPE_CAMPAIGN:
        setCampaigns((current) => sortTags([...current, response.result]));
        break;
      case TAG_TYPE_HOLIDAY:
        setHolidayTypes((current) => sortTags([...current, response.result]));
        break;
    }

    return response.result;
  };

  const handleVerticalChange = (_event: React.SyntheticEvent, verticalId: MarketingVerticalId) => {
    setActiveVerticalId(verticalId);
  };

  const handleHotelsTagTypeChange = (_event: React.SyntheticEvent, tagType: TagType) => {
    setHotelsTagType(tagType);
  };

  const renderHotelsContent = useCallback(
    (vertical: MarketingVerticalConfig) => {
      const isCampaignView = hotelsTagType === TAG_TYPE_CAMPAIGN;

      return (
        <Box>
          <Box sx={{ mb: 4 }}>
            <Tabs
              value={hotelsTagType}
              onChange={handleHotelsTagTypeChange}
              variant="standard"
              sx={{
                minHeight: 40,
                '& .MuiTab-root': {
                  minHeight: 40,
                  px: 2,
                  py: 1,
                },
              }}
            >
              <Tab label="Campaigns" value={TAG_TYPE_CAMPAIGN} />
              <Tab label="Holiday Types" value={TAG_TYPE_HOLIDAY} />
            </Tabs>
          </Box>

          <TagsComponent
            tags={isCampaignView ? campaigns : holidayTypes}
            title={isCampaignView ? 'Campaigns' : 'Holiday Types'}
            type={hotelsTagType}
            vertical={vertical}
            onCreateTag={handleCreateTag}
            onDeleteTag={handleDeleteTag}
            onUpdateTag={handleUpdateTag}
          />
        </Box>
      );
    },
    [campaigns, handleCreateTag, handleDeleteTag, handleUpdateTag, holidayTypes, hotelsTagType],
  );

  const renderVerticalContent = useCallback(
    (vertical: MarketingVerticalConfig) => {
      if (vertical.id === 'hotels') {
        return renderHotelsContent(vertical);
      }

      return (
        <TagsComponent
          tags={campaigns}
          title="Campaigns"
          type={TAG_TYPE_CAMPAIGN}
          vertical={vertical}
          onCreateTag={handleCreateTag}
          onDeleteTag={handleDeleteTag}
          onUpdateTag={handleUpdateTag}
        />
      );
    },
    [campaigns, handleCreateTag, handleDeleteTag, handleUpdateTag, renderHotelsContent],
  );

  const activeVertical =
    MARKETING_VERTICALS.find((vertical) => vertical.id === activeVerticalId) ?? MARKETING_VERTICALS[0];

  if (authLoading || (authenticated && isTagsLoading && !tagsCatalog)) {
    return <Spinner />;
  }

  if (!authenticated) {
    return <ErrorDisplay message="You are not logged in. Please refresh the page or sign in again." />;
  }

  if (isTagsError) {
    const message = tagsError instanceof Error ? tagsError.message : 'Failed to load campaign tags.';

    return (
      <Container>
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => refetchTags()} disabled={isTagsFetching}>
              Retry
            </Button>
          }
          sx={{ mt: 2 }}
        >
          {message}
        </Alert>
      </Container>
    );
  }

  return (
    <Container>
      <Box
        sx={{
          mt: 3,
          mb: 4,
          pt: 3,
          borderTop: 1,
          borderColor: 'divider',
        }}
      >
        <Tabs
          value={activeVerticalId}
          onChange={handleVerticalChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            minHeight: 48,
            '& .MuiTab-root': {
              minHeight: 48,
              px: 2.5,
              py: 1.5,
              mr: 1,
            },
          }}
        >
          {MARKETING_VERTICALS.map((vertical) => (
            <Tab key={vertical.id} label={vertical.label} value={vertical.id} />
          ))}
        </Tabs>
      </Box>

      {renderVerticalContent(activeVertical)}
    </Container>
  );
}
